import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { NAV_LINKS, SITE } from "@/lib/site";
import LogoMark from "@/components/site/LogoMark";

export default function Nav() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="site-nav"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "bg-bone/90 backdrop-blur-md border-b border-rule"
                    : "bg-transparent"
            }`}
        >
            <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 h-16 md:h-20 flex items-center justify-between">
                <Link
                    data-testid="nav-logo"
                    to="/"
                    className="flex items-center gap-3 group"
                    aria-label="Caring for Country — home"
                >
                    <LogoMark
                        size="w-10 h-10 md:w-11 md:h-11"
                        testId="nav-logo-img"
                    />
                    <span className="font-serif text-xl md:text-2xl tracking-tight leading-none">
                        {SITE.name}
                    </span>
                </Link>

                <nav className="hidden lg:flex items-center gap-8">
                    {NAV_LINKS.map((l) => (
                        <Link
                            key={l.href}
                            to={l.href}
                            data-testid={`nav-link-${slugId(l.href)}`}
                            className="text-[12px] tracking-[0.18em] uppercase text-ink2 hover:text-ink transition-colors editorial-link"
                        >
                            {l.label}
                        </Link>
                    ))}
                </nav>

                <Link
                    to="/#contact"
                    data-testid="nav-cta"
                    className="hidden md:inline-flex items-center px-5 py-2.5 bg-ink text-bone text-[11px] tracking-[0.22em] uppercase hover:bg-eucalyptus transition-colors"
                >
                    Work with us
                </Link>

                <button
                    data-testid="nav-toggle"
                    aria-label="Toggle menu"
                    onClick={() => setOpen((v) => !v)}
                    className="lg:hidden w-10 h-10 flex flex-col items-center justify-center gap-1.5"
                >
                    <span
                        className={`block w-5 h-px bg-ink transition-transform ${open ? "translate-y-[3px] rotate-45" : ""}`}
                    />
                    <span
                        className={`block w-5 h-px bg-ink transition-transform ${open ? "-translate-y-[3px] -rotate-45" : ""}`}
                    />
                </button>
            </div>

            {open && (
                <div
                    data-testid="nav-mobile-panel"
                    className="lg:hidden bg-bone border-t border-rule"
                >
                    <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-8 flex flex-col gap-5">
                        {NAV_LINKS.map((l) => (
                            <Link
                                key={l.href}
                                to={l.href}
                                onClick={() => setOpen(false)}
                                className="text-base tracking-wide text-ink hover:text-ochre transition-colors"
                                data-testid={`nav-link-mobile-${slugId(l.href)}`}
                            >
                                {l.label}
                            </Link>
                        ))}
                        <Link
                            to="/#contact"
                            onClick={() => setOpen(false)}
                            data-testid="nav-cta-mobile"
                            className="mt-2 inline-flex w-fit px-5 py-3 bg-ink text-bone text-[11px] tracking-[0.22em] uppercase"
                        >
                            Work with us
                        </Link>
                    </div>
                </div>
            )}
        </header>
    );
}

function slugId(href) {
    // "/#why" -> "why", "/insights" -> "insights"
    if (href.startsWith("/#")) return href.slice(2);
    return href.replace(/^\//, "") || "home";
}
